import { useState, useCallback, useEffect } from 'react';
import { GameState, MatchEvent, Player, Result } from '../domain/entities';
import { hasAutosave as hasAutosaveInDb, saveAutosave, getAutosave, hasLegacySave, importLegacySave } from '../infrastructure/GameStateRepository';
import { GAME_VERSION } from '../version';
import { migrate } from '../services/MigrationService';
import { useGameSetup } from './hooks/useGameSetup';
import { useGameSimulation } from './hooks/useGameSimulation';
import { useClubManagement } from './hooks/useClubManagement';
import { usePersistence } from './hooks/usePersistence';
import { useUtility } from './hooks/useUtility';
import { mensGameConfig } from '../data/mens/config';
import { womensGameConfig } from '../data/womens/config';
import { PlayerGenerator } from '../services/playerGenerator';

const initialGameState: Omit<GameState, 'gameStarted' | 'leagues' | 'fixtures' | 'cups' | 'playersForSale' | 'managerHistory' | 'transferHistory' | 'news'> & { gameStarted: false; leagues: {}; fixtures: {}; cups: {}; playersForSale: never[]; managerHistory: never[]; transferHistory: never[]; news: never[]; } = {
    version: GAME_VERSION,
    gameStarted: false,
    season: 0,
    currentWeek: 0,
    playerTeamName: '',
    playerLeagueName: '',
    leagues: {},
    fixtures: {},
    cups: {},
    playersForSale: [],
    gameOver: false,
    managerHistory: [],
    transferHistory: [],
    news: [],
    gameType: 'mens',
    config: mensGameConfig,
};

export const useGameLogic = () => {
    const [gameState, setGameState] = useState<GameState | { gameStarted: false }>(initialGameState);
    const [isLoading, setIsLoading] = useState(true);
    const [isSimulating, setIsSimulating] = useState(false);
    // FIX: Add missing simulationTitle state.
    const [simulationTitle, setSimulationTitle] = useState('Simulating...');
    const [simulationProgress, setSimulationProgress] = useState<{ title: string, playerMatchEvents: MatchEvent[] | null, result: Result | null }>({ title: '', playerMatchEvents: null, result: null });
    const [hasSave, setHasSave] = useState(false);
    const [sackedInfo, setSackedInfo] = useState<any | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);

    useEffect(() => {
        const init = async () => {
            setIsLoading(true);
            const legacySaveExists = await hasLegacySave();
            if (legacySaveExists) {
                await importLegacySave();
            }

            const autosaveExists = await hasAutosaveInDb();
            setHasSave(autosaveExists);
            if (autosaveExists) {
                const loadedGame = await getAutosave();
                if (loadedGame) {
                    try {
                        const migratedState = migrate(loadedGame);
                        if (migratedState.gameStarted) {
                            PlayerGenerator.initializePlayerIdCounter(migratedState);
                            setGameState(migratedState);
                        }
                    } catch (e: any) {
                        console.error("Failed to migrate autosave:", e.message);
                    }
                }
            }
            setIsLoading(false);
        };
        init();
    }, []);

    useEffect(() => {
        if (gameState.gameStarted) {
            saveAutosave(gameState as GameState);
            setHasSave(true);
        }
    }, [gameState]);
    
    const gameSetup = useGameSetup({ setGameState, setIsLoading, setHasSave, onError: setLoadError });
    // FIX: Pass setSimulationTitle to useGameSimulation hook.
    const simulation = useGameSimulation({ gameState, setGameState, setIsSimulating, setSimulationTitle, setSimulationProgress, setSackedInfo, isSimulating });
    const management = useClubManagement({ gameState, setGameState });
    const persistence = usePersistence({ gameState, setGameState });
    const utility = useUtility({ gameState, setGameState, setHasSave });

    const patchMissingConfig = useCallback(() => {
        setGameState(prevState => {
            if (prevState.gameStarted && !prevState.config) {
                const newState = JSON.parse(JSON.stringify(prevState)) as GameState;
                newState.config = newState.gameType === 'womens' ? womensGameConfig : mensGameConfig;
                return newState;
            }
            return prevState;
        });
    }, [setGameState]);

    return {
        gameState,
        isLoading,
        isSimulating,
        simulationProgress,
        sackedInfo,
        hasSave,
        loadError,
        ...gameSetup,
        ...simulation,
        ...management,
        ...persistence,
        ...utility,
        patchMissingConfig,
    };
};
